import { useRef } from "react";
import useExceptionHandler from "../../Hooks/useExceptionHandler";
import useMessage from "../../Hooks/useMessage";
import { buscar } from "../../Utils/Buscar";
import { PROCESAR_PAGO_TC } from "../../Utils";
import "./PagoTarjetaFormalizacion.css";

const CreditCardIcon = () => (
  <svg className="card-payment-dialog__icon" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M2 7h20v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2Z" />
    <path d="M2 11h20" />
  </svg>
);

const CloseIcon = () => (
  <svg className="card-payment-dialog__close-icon" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg className="card-payment-dialog__action-icon" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

export function PagoTarjetaFormalizacionDialog({ visible, onHide, ...props }) {
  if (!visible) {
    return null;
  }

  return (
    <div className="modal-overlay formalizacion-card-payment-modal" role="dialog" aria-modal="true" aria-labelledby="card-payment-title">
      <div className="modal">
        <header className="modal__header">
          <h3 id="card-payment-title">
            <CreditCardIcon />
            Pago con tarjeta
          </h3>
          <button className="modal__close" type="button" onClick={onHide} aria-label="Cerrar">
            <CloseIcon />
          </button>
        </header>
        <PagoTarjetaFormalizacion {...props} onCancel={onHide} />
      </div>
    </div>
  );
}

export function PagoTarjetaFormalizacion({
  setPagoExitoso,
  total,
  impuesto = 0,
  showLoading,
  hideLoading,
  onCancel,
  submitLabel = "Proceder al pago",
  keepLoadingOnSuccess = false,
}) {
  const cardNumberRef = useRef(null);
  const cardExpiryRef = useRef(null);
  const cardCvvRef = useRef(null);
  const cardNameRef = useRef(null);
  const consultaActualId = useRef();
  const { Error, Message, Warning } = useMessage();
  const { handleException } = useExceptionHandler();

  const formatMoney = (value) =>
    // eslint-disable-next-line no-undef
    `RD$${Citrus.Convert.toCurrencyNoSimbolo(value || 0)}`;

  const handleCardNumberChange = (event) => {
    const cardNumber = event.target.value.replace(/\D/g, "").substring(0, 19);
    const isAmex = /^3[47]/.test(cardNumber);
    event.target.value = isAmex
      ? cardNumber.replace(/(\d{4})(\d{6})(\d{5})/, "$1 $2 $3")
      : cardNumber.replace(/(\d{4})(?=\d)/g, "$1 ");
  };

  const handleExpDateChange = (event) => {
    const expDate = event.target.value.replace(/\D/g, "").substring(0, 4);
    event.target.value = expDate.replace(/(\d{2})(?=\d)/g, "$1/");
  };

  const handleCvvChange = (event) => {
    event.target.value = event.target.value.replace(/\D/g, "").substring(0, 4);
  };

  const validarTarjeta = () => {
    const messages = [];
    const expDate = cardExpiryRef.current?.value || "";
    const enteredMonth = parseInt(expDate.substring(0, 2), 10);
    const enteredYear = parseInt(expDate.substring(3, 5), 10);
    const enteredDate = new Date(`20${enteredYear}`, enteredMonth - 1, 1);

    if (!cardNameRef.current?.value) messages.push("Nombre del titular es requerido");
    if (!cardNumberRef.current?.value) messages.push("Numero de tarjeta es requerido");
    if (!expDate) messages.push("Expiracion es requerida");
    if (expDate && !(enteredMonth <= 12)) messages.push("Fecha de expiracion incorrecta: Mes incorrecto");
    if (expDate && !(enteredDate > new Date())) messages.push("Fecha de expiracion incorrecta: La tarjeta esta vencida");
    if (!cardCvvRef.current?.value || cardCvvRef.current.value.length < 3) messages.push("CVV es incorrecto");

    if (messages.length > 0) {
      Warning({ messages });
    }
    return messages.length === 0;
  };

  const procesarPago = (event) => {
    event.preventDefault();
    if (!validarTarjeta()) {
      return;
    }

    onCancel?.();
    showLoading();
    let pagoAprobado = false;
    const consultaId = Math.random();
    consultaActualId.current = consultaId;

    buscar(PROCESAR_PAGO_TC, {
      request: {
        Origen: "M",
        IdSec: null,
        numero: cardNumberRef.current.value,
        cvv: cardCvvRef.current.value,
        fecha: cardExpiryRef.current.value,
        montoTotal: total,
        impuestos: impuesto,
      },
    })
      .then((data) => {
        if (consultaId === consultaActualId.current) {
          if (data?.ErrorMessages?.length > 0) {
            Error({ messages: data.ErrorMessages });
            setPagoExitoso(false);
          } else if (data?.WarningMessages?.length > 0) {
            Warning({ messages: data.WarningMessages });
            setPagoExitoso(false);
          } else {
            Message({ messages: data?.Messages || ["Pago procesado exitosamente."] });
            pagoAprobado = true;
            setPagoExitoso(true);
          }
        }
      })
      .catch((error) => {
        handleException(error);
        setPagoExitoso(false);
      })
      .finally(() => {
        if (!pagoAprobado || !keepLoadingOnSuccess) {
          hideLoading();
        }
      });
  };

  return (
    <>
      <div className="modal__body card-payment-modal">
        <img
          className="card-payment-modal__brands"
          src="/Commons/Scripts/Componentes/Formalizar/prototype/wizard/assets/images/tarjetas.png"
          alt="Visa, MasterCard y American Express"
        />
        <p className="card-payment-modal__copy">Completa el pago seguro con tarjeta antes de formalizar la cuenta.</p>
        <div className="amount-box">
          <span>Monto a pagar</span>
          <strong>{formatMoney(total)}</strong>
        </div>
        <div className="card-payment-form">
          <div className="field">
            <label className="field__label" htmlFor="cardNameMiCuenta">
              Nombre del titular <span className="req">*</span>
            </label>
            <input className="input" id="cardNameMiCuenta" ref={cardNameRef} type="text" autoComplete="cc-name" />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="cardNumberMiCuenta">
              {"N\u00famero de tarjeta"} <span className="req">*</span>
            </label>
            <input
              className="input"
              id="cardNumberMiCuenta"
              ref={cardNumberRef}
              type="text"
              inputMode="numeric"
              autoComplete="cc-number"
              maxLength="19"
              onChange={handleCardNumberChange}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="cardExpiryMiCuenta">
              {"Expiraci\u00f3n"} <span className="req">*</span>
            </label>
            <input
              className="input"
              id="cardExpiryMiCuenta"
              ref={cardExpiryRef}
              type="text"
              inputMode="numeric"
              autoComplete="cc-exp"
              placeholder="MM/AA"
              maxLength="5"
              onChange={handleExpDateChange}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="cardCvvMiCuenta">
              CVV <span className="req">*</span>
            </label>
            <input
              className="input"
              id="cardCvvMiCuenta"
              ref={cardCvvRef}
              type="password"
              inputMode="numeric"
              autoComplete="cc-csc"
              maxLength="4"
              onChange={handleCvvChange}
            />
          </div>
        </div>
      </div>
      <footer className="modal__footer">
        <button className="btn btn--ghost" type="button" onClick={onCancel}>
          Cancelar
        </button>
        <button className="btn btn--primary" type="button" onClick={procesarPago}>
          {submitLabel} <ArrowRightIcon />
        </button>
      </footer>
    </>
  );
}
